import React, { Component } from 'react';
import queryString from 'query-string';
import { connect, ConnectedProps } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Form } from 'react-final-form';
import { login, setLoading } from '../redux/actions';
import { redirectExternalUrl } from '../utilities/urlHelper';
import NotificationBox from '../components/layout/NotificationBox';
import ReactFinalFormWrapper, { composeValidators, requiredField, validEmail } from '../components/register/ReactFinalFormWrapper';
import { RootState } from '../redux/types';
import withRouter, { RouteType } from '../hoc/withRouter';
import NewQuipReleaseNotification from '../components/layout/NewQuipNotification';
import { QCC_URL } from '../environment';

const LOGIN_ERROR = {
  USER_DOES_NOT_EXIST: 'login.userdoesnotexist',
  USER_NOT_ACTIVATE: 'login.usernotactivate',
  EMAIL_NOT_VERIFIED: 'login.emailNotActivated',
  PASSWORD_FALSE: 'login.invalidCredentials',
  SWITCH_GERMANY: 'login.switchToGermany',
  SWITCH_INTERNATIONAL: 'login.switchToInternational',
};

type State = {
  loginFailedMessage: string;
  internetExplorerCheck: boolean;
}

class LoginView extends Component<Props, State> {
  static isIE = () => {
    const ua = navigator.userAgent;
    /* MSIE used to detect old browsers and Trident used to newer ones */
    return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
  };

  static handleOpenQCC() {
    window.open(`${QCC_URL}?direct_kcl=true`, '_blank', 'noopener,noreferrer');
  }

  formConfiguration = {
    label: <FormattedMessage id="registration.personalInformationHeading" />,
    formRef: 'billing_form',
    fields: [
      {
        label: this.props.intl.formatMessage({ id: 'registration.userEmail' }),
        key: 'email',
        type: 'text',
        validate: composeValidators(requiredField, validEmail),
      },
      {
        label: this.props.intl.formatMessage({ id: 'resetPassword.passwordLabel' }),
        key: 'password',
        type: 'password',
        validate: requiredField,
      },
    ],
    requiredMessage: <FormattedMessage id="registration.requiredMessage" />,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      loginFailedMessage: '',
      internetExplorerCheck: LoginView.isIE(),
    };
  }

  handleLoggedIn = (): string => {
    const { router, doSetLoading } = this.props;
    const queryParams = queryString.parse(router.location.search);

    // global method in TYPO3 site to set the username in header
    if ((window as any).displayZerpaUserName) {
      (window as any).displayZerpaUserName();
    }

    if (queryParams.redirectUrl) {
      doSetLoading(true);
      redirectExternalUrl(queryParams.redirectUrl);
      return '';
    }
    let redirectUri = '';
    if (queryParams?.redirect === 'trials') {
      const selectParam = queryParams?.select ? `&select=${queryParams?.select}` : '';
      redirectUri = `/${queryParams.redirect}?trialId=${queryParams.trialId}${selectParam}`;
    } else if (queryParams?.redirect === 'download') {
      redirectUri = `/${queryParams.redirect}?containerName=${queryParams.containerName}&fileName=${queryParams.fileName}`;
    } else if (queryParams?.redirect === 'voucherenroll') {
      redirectUri = `/${queryParams.redirect}`;
    } else {
      redirectUri = '/user';
    }
    return redirectUri;
  };

  login = ({ email, password }: { email: string, password: string }) => {
    const {
      intl,
      doLogin,
    } = this.props;

    const { internetExplorerCheck } = this.state;

    if (internetExplorerCheck) {
      // eslint-disable-next-line no-alert
      alert(intl.messages['login.internetExplorerWarning']);
    }

    doLogin(email.trim(), password.trim()).catch(
      (error: Error) => {
        const errorCode = error.message && error.message.split('QUIP_USER_LOGIN_')[1];
        const textId = LOGIN_ERROR[errorCode as keyof typeof LOGIN_ERROR];
        let loginFailedMessage = intl.formatMessage({ id: 'login.generalloginerror' });
        if (textId) {
          loginFailedMessage = intl.formatMessage({ id: textId }, { email });
        }

        this.setState({ loginFailedMessage });
      },
    );
  };

  renderLoginForm = () => (
    <>
      <Form
        onSubmit={this.login}
        render={({ form, handleSubmit }) => {
          const containsError = Object.keys(form.getState().errors || {}).length > 0;
          return (
            <form onSubmit={handleSubmit}>
              <ReactFinalFormWrapper registrationForm={this.formConfiguration} />
              <Box
                sx={{
                  padding: '0.25rem',
                }}
              >
                <Button
                  variant="contained"
                  id="LoginButton-login"
                  type="submit"
                  sx={{
                    color: 'white',
                    width: '100%',
                    backgroundColor: 'primary.main',
                    // mx: '0.25rem',
                  }}
                  disabled={containsError}
                >
                  <FormattedMessage id="menu.login" />
                </Button>
              </Box>
            </form>
          );
        }}
      />
    </>
  );

  renderLoginFailed = () => {
    const { loginFailedMessage } = this.state;
    return (
      <div style={{ marginTop: 10 }}>
        <p>{loginFailedMessage}</p>
        <Button
          variant="contained"
          id="LoginButton-login"
          onClick={() => this.setState({ loginFailedMessage: '' })}
          sx={{
            color: 'white',
            width: '100%',
            backgroundColor: 'primary.main',
          }}
        >
          <FormattedMessage id="login.tryAgain" />
        </Button>
      </div>
    );
  };

  static renderQCCPanel() {
    return (
      <Paper
        sx={{
          width: 400,
          padding: '15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          minHeight: 300,
        }}
        variant="outlined"
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          <FormattedMessage id="login.qccTitle" />
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          <FormattedMessage id="login.qccDescription" />
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={LoginView.handleOpenQCC}
          endIcon={<OpenInNewIcon />}
          sx={{
            color: 'white',
            width: '100%',
            backgroundColor: 'primary.main',
          }}
        >
          <FormattedMessage id="login.qccButton" />
        </Button>
      </Paper>
    );
  }

  render() {
    const { internetExplorerCheck, loginFailedMessage } = this.state;
    const { tokenId, email, router } = this.props;
    const queryParams = queryString.parse(router.location.search);

    if (tokenId && email) {
      const redirectUri = this.handleLoggedIn();
      return <Navigate to={redirectUri} />;
    }

    return (
      <Box
        sx={{
          padding: 0.5,
          display: 'flex',
          justifyContent: 'center',
          minWidth: '100vw',
        }}
      >
        {
          internetExplorerCheck
          && (
            <NotificationBox>
              <FormattedMessage id="login.internetExplorerWarning" />
            </NotificationBox>
          )
        }
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {/* Left Panel - Old Zerpa Login */}
          <Paper
            sx={{
              width: 400,
              padding: '15px',
            }}
            variant="outlined"
          >
            <NewQuipReleaseNotification />
            {
              queryParams && queryParams.reason === 'unauthorize'
              && (<h4 style={{ color: 'red' }}><FormattedMessage id="login.expired" /></h4>)
            }
            <Typography variant="h5"><FormattedMessage id="menu.login" /></Typography>
            {loginFailedMessage ? this.renderLoginFailed() : this.renderLoginForm()}
            <div>
              <p>
                <FormattedMessage id="login.signupLabel" />
                <Box
                  component="span"
                  sx={{ ml: 0.5, cursor: 'pointer', color: 'primary.main' }}
                  role="link"
                  onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    e.preventDefault();
                    router.navigate('/register');
                  }}
                >
                  <FormattedMessage id="login.signupLink" />
                </Box>
              </p>
              <p>
                <FormattedMessage id="login.forgotPasswordLabel" />
                <Box
                  role="link"
                  component="span"
                  sx={{ ml: 0.5, cursor: 'pointer', color: 'primary.main' }}
                  onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    e.preventDefault();
                    router.navigate('/passwordRequest');
                  }}
                >
                  <FormattedMessage id="login.requestPasswordLink" />
                </Box>
              </p>
            </div>
          </Paper>

          {/* Vertical Divider with OR text */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'stretch',
              px: 2,
            }}
          >
            <Box sx={{ flex: 1, width: '1px', bgcolor: 'divider' }} />
            <Typography
              variant="body2"
              sx={{
                py: 1.5,
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              <FormattedMessage id="login.or" />
            </Typography>
            <Box sx={{ flex: 1, width: '1px', bgcolor: 'divider' }} />
          </Box>

          {/* Right Panel - QCC */}
          {LoginView.renderQCCPanel()}
        </Box>
      </Box>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  tokenId: state.users.token && state.users.token.id,
  email: state.users.token && state.users.token.email,
});

const mapDispatchToProps = (dispatch: any) => ({
  doLogin: (email: string, password: string) => dispatch(login(email, password)),
  doSetLoading: (value: boolean) => dispatch(setLoading(value)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & RouteType & {
  intl: {
    formatMessage: (arg: Record<string, string>, arg2?: Record<string, string>) => string;
    messages: Record<string, string>;
  };
}
export default injectIntl(connector(withRouter(LoginView)));
